import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Dashboard feed data sources — the tabbed document/asset feeds + the revenue
 * dashboard, mirrored from datacentral-client-v4 `modules/Dashboard/services`
 * and `modules/Asset/services`. All DataCentral v4 endpoints, served with the
 * UMA RPT (handled by `dataCentralApi`).
 */

/* ── Documents (Announcements feed) ─────────────────────────────────────── */
/** A shared document row (announcement / regulation / other). */
export interface FeedDocument {
  document_id: number;
  name_file: string;
  descriptions?: string;
  employee_name?: string;
  created_at?: string;
  document_type?: string;
}

/** Tabs of the Announcements widget → the `document_type` each queries. */
export type AnnouncementTab = 'NOTIFICATION' | 'REGULATION' | 'OTHER';

/** Documents by type — one call per Announcements tab. */
export function useDocumentsByType(documentType: AnnouncementTab) {
  return useQuery({
    queryKey: ['dashboard', 'documents-by-type', documentType],
    queryFn: async (): Promise<FeedDocument[]> => {
      const res = await dataCentralApi.get<ResponseArray<FeedDocument>>(
        `/documents/document-by-type?document_type=${documentType}`,
      );
      return res.data?.results ?? [];
    },
  });
}

/* ── Document statistics (tab count badges) ─────────────────────────────── */
interface DocStatItem {
  total_count: number;
}

/** `/documents/statistical-dashboard` → per-type totals for tab badges. */
export interface DocumentStatistics {
  NOTIFICATION?: DocStatItem;
  REGULATION?: DocStatItem;
  OTHER?: DocStatItem;
}

export function useDocumentStatistics() {
  return useQuery({
    queryKey: ['dashboard', 'documents-statistics'],
    queryFn: async (): Promise<DocumentStatistics | null> => {
      const res = await dataCentralApi.get<ResponseObject<DocumentStatistics>>(
        '/documents/statistical-dashboard',
      );
      return res.data?.result ?? null;
    },
    staleTime: 5 * 60_000,
  });
}

/* ── Internal assets (Internal status feed) ─────────────────────────────── */
/** Document groups powering the Internal status tabs. */
export type AssetGroup = 'HR_CULTURE' | 'PROCEDURE_GUIDE' | 'LEGAL_ADMIN';

/** An asset (internal document) row from `/assets/dashboard`. */
export interface FeedAsset {
  asset_id: string;
  asset_name: string;
  asset_slug?: string;
  asset_description?: string;
  created_by_name?: string;
  department_name?: string;
  updated_at?: string;
  current_version?: {
    document_type?: string;
    version_code?: string;
    asset_version_status?: string;
  };
}

/** Internal assets by document group — one call per Internal status tab. */
export function useAssetsByGroup(group: AssetGroup) {
  return useQuery({
    queryKey: ['dashboard', 'assets-dashboard', group],
    queryFn: async (): Promise<FeedAsset[]> => {
      const res = await dataCentralApi.get<ResponseArray<FeedAsset>>(
        `/assets/dashboard?document_group=${group}`,
      );
      return res.data?.results ?? [];
    },
  });
}

/* ── Revenue dashboard (summary + monthly comparison) ───────────────────── */
export interface RevenueMonthly {
  half_year?: string;
  quarter?: string;
  /** VND, string number. */
  period_target: string;
  /** VND, string number. */
  period_actual: string;
  period_completion_percentage?: number;
}

export interface RevenueDashboardSummary {
  total_target_amount: number;
  total_actual_amount: number;
  overall_completion: number;
}

export interface RevenueDashboard {
  summary: RevenueDashboardSummary;
  monthly: RevenueMonthly[];
}

const EMPTY_REVENUE: RevenueDashboard = {
  summary: { total_target_amount: 0, total_actual_amount: 0, overall_completion: 0 },
  monthly: [],
};

/**
 * Revenue dashboard for a `YYYY-MM` month range. Returns both the totals
 * summary and the monthly_comparison array (for the mini bar breakdown).
 */
export function useRevenueDashboardRange(gte: string, lte: string) {
  return useQuery({
    queryKey: ['dashboard', 'revenue-range', gte, lte],
    queryFn: async (): Promise<RevenueDashboard> => {
      const res = await dataCentralApi.get<
        ResponseObject<{ summary?: RevenueDashboardSummary; monthly_comparison?: RevenueMonthly[] }>
      >(`/dashboard?gte=${gte}&lte=${lte}`);
      const result = res.data?.result;
      if (!result) return EMPTY_REVENUE;
      return {
        summary: result.summary ?? EMPTY_REVENUE.summary,
        monthly: result.monthly_comparison ?? [],
      };
    },
  });
}
